import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useAuth } from "../context/AuthContext";
import {
  Target,
  MessageCircle,
  Puzzle,
  Rocket,
  Heart,
  TrendingUp,
  Palette,
  Trophy,
  Search,
  Folder,
  Clock,
  Handshake,
  Lightbulb,
  Globe,
  Star,
} from "lucide-react";

const defaultTimetableData = [
  {
    no: 1,
    objective: "Exploring products and services",
    start: "10:00 AM",
    end: "11:00 AM",
  },
  {
    no: 2,
    objective: "Customer Acquisition +Account openings",
    start: "11:00 AM",
    end: "01:00 PM",
  },
  { no: 3, objective: "Lunch Break", start: "01:00 PM", end: "01:40 PM" },
  {
    no: 4,
    objective: "Target customers marketing and closing the sales",
    start: "02:00 PM",
    end: "04:00 PM",
  },
  {
    no: 5,
    objective: "Digital marketing and social media activities",
    start: "04:00 PM",
    end: "06:00 PM",
  },
  {
    no: 6,
    objective: "Meetings and collaborations",
    start: "06:00 PM",
    end: "07:00 PM",
  },
  { no: 7, objective: "Gym", start: "07:00 PM", end: "08:00 PM" },
  { no: 8, objective: "Dinner", start: "08:00 PM", end: "09:00 PM" },
  { no: 9, objective: "Reading Books", start: "09:00 PM", end: "10:00 PM" },
];

const defaultPieData = [
  { name: "Office Working", value: 7, color: "#C94BFF" },
  { name: "Sleep", value: 7, color: "#3CFF8F" },
  { name: "Food and Refreshment", value: 7, color: "#FF8C1A" },
  { name: "Explore New Things", value: 3, color: "#FFD600" },
];

const LegendDot = ({ color }) => (
  <span
    className="inline-block w-3 h-3 rounded-full mr-2.5 mt-0.5"
    style={{ background: color }}
  />
);

// SVG Trash Icon
const TrashIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FF4B4B"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="7" width="16" height="13" rx="2" />
    <path d="M8 7V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const OfficeNoticeBoard = () => {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [timetable, setTimetable] = useState(defaultTimetableData);
  const [editRows, setEditRows] = useState(defaultTimetableData);
  const [pieData, setPieData] = useState(defaultPieData);
  const [pieModalOpen, setPieModalOpen] = useState(false);
  const [editPieRows, setEditPieRows] = useState(defaultPieData);

  const openModal = () => {
    setEditRows(timetable.map((row) => ({ ...row })));
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  const openPieModal = () => {
    setEditPieRows(pieData.map((row) => ({ ...row })));
    setPieModalOpen(true);
  };
  const closePieModal = () => setPieModalOpen(false);

  const handleRowChange = (idx, field, value) => {
    setEditRows((rows) =>
      rows.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
  };
  const handleAddRow = () => {
    setEditRows((rows) => [
      ...rows,
      { no: rows.length + 1, objective: "", start: "", end: "" },
    ]);
  };
  const handleDeleteRow = (idx) => {
    setEditRows((rows) =>
      rows.filter((_, i) => i !== idx).map((row, i) => ({ ...row, no: i + 1 }))
    );
  };
  const handleSave = () => {
    setTimetable(editRows);
    setModalOpen(false);
    // Backend integration will go here
  };

  // Pie chart edit handlers
  const handlePieRowChange = (idx, field, value) => {
    setEditPieRows((rows) =>
      rows.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
  };
  const handleAddPieRow = () => {
    setEditPieRows((rows) => [
      ...rows,
      { name: "", value: 1, color: "#FFD600" },
    ]);
  };
  const handleDeletePieRow = (idx) => {
    setEditPieRows((rows) => rows.filter((_, i) => i !== idx));
  };
  const handlePieSave = () => {
    setPieData(editPieRows);
    setPieModalOpen(false);
    // Backend integration will go here
  };

  return (
    <div className="bg-black min-h-screen text-white font-inter relative">
      {/* Main Content */}
      <div className="flex flex-col lg:flex-row md:flex-row gap-3 px-3 pt-3">
        {/* Left: Office Time Table */}
        <div className="flex-grow w-full lg:w-2/3 md:w-2/3 bg-[#181818] rounded-[10px] p-6 min-w-0 relative">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-[20px] font-bold text-white">
              Office Time Table
            </h2>
            {(user?.role === "admin" || user?.role === "subAdmin") && (
              <button
                className="bg-[#FFD600] hover:bg-[#ffdc40] text-[#000] font-semibold text-[14px] py-2 px-3 rounded-md flex items-center gap-2"
                onClick={openModal}
              >
                Edit Timetable <span className="text-[16px]">✎</span>
              </button>
            )}
          </div>
          <div className="overflow-x-auto rounded-md bg-[#111] border border-[#333] scrollbar-hide">
            <table className="w-full border-collapse min-w-[600px] text-sm">
              <thead>
                <tr>
                  <th className="py-3 px-2 border border-[#333] bg-[#181818] text-[#FFD600] font-semibold">
                    S. NO.
                  </th>
                  <th className="py-3 px-2 border border-[#333] bg-[#181818] text-[#FFD600] font-semibold">
                    WORKING OBJECTIVE
                  </th>
                  <th className="py-3 px-2 border border-[#333] bg-[#181818] text-[#FFD600] font-semibold">
                    TIME (S)
                  </th>
                  <th className="py-3 px-2 border border-[#333] bg-[#181818] text-[#FFD600] font-semibold">
                    TIME (E)
                  </th>
                </tr>
              </thead>
              <tbody>
                {timetable.map((row, idx) => (
                  <tr
                    key={row.no}
                    className={idx % 2 === 0 ? "bg-[#181818]" : "bg-[#222]"}
                  >
                    <td className="py-2 px-2 border border-[#333] text-white font-semibold">
                      {row.no}.
                    </td>
                    <td className="py-2 px-2 border border-[#333] text-white">
                      {row.objective}
                    </td>
                    <td className="py-2 px-2 border border-[#333] text-white">
                      {row.start}
                    </td>
                    <td className="py-2 px-2 border border-[#333] text-white">
                      {row.end}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: Smart Planning */}
        <div className="w-full lg:w-1/3 md:w-1/3 bg-[#181818] rounded-[10px] p-4 min-w-0">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-[18px] font-bold text-white">Smart Planning</h2>
            {(user?.role === "admin" || user?.role === "subAdmin") && (
              <button
                className="bg-[#FFD600] hover:bg-[#ffdc40] text-[#000] font-semibold text-[14px] py-2 px-3 rounded-md flex items-center gap-2"
                onClick={openPieModal}
              >
                Edit <span className="text-[16px]">✎</span>
              </button>
            )}
          </div>
          <div className="flex flex-col items-center justify-start">
            <div className="w-[200px] h-[200px] mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    isAnimationActive={true}
                  >
                    {pieData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} hrs`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="w-full bg-[#111] text-sm rounded-lg p-3 mt-2 border border-[#333]">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-[#FFD600] font-semibold">
                    <th className="text-left py-1 border-b border-[#333]">
                      ACTIVITY
                    </th>
                    <th className="text-left py-1 border-b border-[#333]">
                      TIME IN A DAY
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pieData.map((item, idx) => (
                    <tr key={item.name} className="text-white font-normal">
                      <td className="py-1 flex items-center gap-2 text-xs">
                        <LegendDot color={item.color} />
                        {item.name}
                      </td>
                      <td className="py-1 text-xs">{item.value} hrs a day</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Core Values Section */}
      <div className="p-8">
        <h2 className="text-[22px] font-bold mb-6 text-white">
          Core Values For Team Management
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {/* 1 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Target className="w-6 h-6" /> Strategic Creation
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Plan with purpose and direction</li>
              <li>Use resources wisely</li>
              <li>Align with long-term vision</li>
            </ul>
          </div>
          {/* 2 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <MessageCircle className="w-6 h-6" /> Communication
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Listen actively</li>
              <li>Express clearly</li>
              <li>Foster openness</li>
            </ul>
          </div>
          {/* 3 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Puzzle className="w-6 h-6" /> Problem Solving
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Approach with logic</li>
              <li>Use data and insight</li>
              <li>Be solution-oriented</li>
            </ul>
          </div>
          {/* 4 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Rocket className="w-6 h-6" /> Guerrilla Marketing
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Be bold and creative</li>
              <li>Leverage low-cost strategies</li>
              <li>Think outside the box</li>
            </ul>
          </div>
          {/* 5 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Heart className="w-6 h-6" /> Emotional Marketing
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Connect with audience feelings</li>
              <li>Build trust and loyalty</li>
              <li>Craft emotional stories</li>
            </ul>
          </div>
          {/* 6 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <TrendingUp className="w-6 h-6" /> Marketing Expert
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Know your audience</li>
              <li>Use right tools and platforms</li>
              <li>Track and analyze performance</li>
            </ul>
          </div>
          {/* 7 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Palette className="w-6 h-6" /> Creative Thinking
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Break conventional patterns</li>
              <li>Pitch new concepts</li>
              <li>Encourage innovation</li>
            </ul>
          </div>
          {/* 8 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Trophy className="w-6 h-6" /> Perfection in the Work
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Pay attention to detail</li>
              <li>Aim for excellence</li>
              <li>Deliver with consistency</li>
            </ul>
          </div>
          {/* 9 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Search className="w-6 h-6" /> Analysis
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Understand the 'why' behind results</li>
              <li>Support decisions with data</li>
              <li>Improve through review</li>
            </ul>
          </div>
          {/* 10 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Folder className="w-6 h-6" /> Organizing the Thing
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Structure tasks logically</li>
              <li>Set priorities</li>
              <li>Keep workflow clean</li>
            </ul>
          </div>
          {/* 11 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Clock className="w-6 h-6" /> Time and Configuration
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Respect deadlines</li>
              <li>Balance workload</li>
              <li>Use time-tracking tools</li>
            </ul>
          </div>
          {/* 12 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Handshake className="w-6 h-6" /> Keep It Professional
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Respect roles and boundaries</li>
              <li>Follow through on commitments</li>
              <li>Show workplace etiquette</li>
            </ul>
          </div>
          {/* 13 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Lightbulb className="w-6 h-6" /> Think Before You Do
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Evaluate outcomes</li>
              <li>Prevent errors</li>
              <li>Act with purpose</li>
            </ul>
          </div>
          {/* 14 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Globe className="w-6 h-6" /> Think About Future
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Make scalable choices</li>
              <li>Prepare for change</li>
              <li>Adapt fast</li>
            </ul>
          </div>
          {/* 15 */}
          <div className="bg-[#FFCA201A] rounded-[10px] p-6 opacity-[0.85]">
            <div className="font-bold mb-4 text-[#FFD600] flex items-center gap-2 text-lg">
              <Star className="w-6 h-6" /> Ideology
            </div>
            <ul className="text-white list-disc pl-5 text-sm space-y-1">
              <li>Stick to LONO values</li>
              <li>Lead with belief</li>
              <li>Stay consistent in thought</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modal Popup */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center">
          <div className="bg-[#181818] rounded-xl p-8 min-w-[600px] max-w-[90vw] shadow-2xl">
            <h2 className="text-[#FFD600] mb-4.5">Edit Office Time Table</h2>
            <table className="w-full border-collapse mb-4.5">
              <thead>
                <tr className="text-[#FFD600] font-bold text-[15px]">
                  <th className="p-2 text-left w-[60px]">S. No.</th>
                  <th className="p-2 text-left">WORKING OBJECTIVE</th>
                  <th className="p-2 text-left w-[120px]">TIME (S)</th>
                  <th className="p-2 text-left w-[120px]">TIME (E)</th>
                  <th className="w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {editRows.map((row, idx) => (
                  <tr key={row.no}>
                    <td className="p-1.5 font-semibold">{row.no}.</td>
                    <td className="p-1.5">
                      <input
                        value={row.objective}
                        onChange={(e) =>
                          handleRowChange(idx, "objective", e.target.value)
                        }
                        className="w-full p-1.5 rounded border border-[#333] bg-[#222] text-white"
                        placeholder="Working Objective"
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        value={row.start}
                        onChange={(e) =>
                          handleRowChange(idx, "start", e.target.value)
                        }
                        className="w-full p-1.5 rounded border border-[#333] bg-[#222] text-white"
                        placeholder="Start Time"
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        value={row.end}
                        onChange={(e) =>
                          handleRowChange(idx, "end", e.target.value)
                        }
                        className="w-full p-1.5 rounded border border-[#333] bg-[#222] text-white"
                        placeholder="End Time"
                      />
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => handleDeleteRow(idx)}
                        className="bg-transparent border-none text-[#FF4B4B] text-lg cursor-pointer flex items-center justify-center"
                        title="Delete Row"
                        disabled={editRows.length === 1}
                      >
                        <TrashIcon size={28} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-3 mb-4.5">
              <button
                onClick={handleAddRow}
                className="bg-[#FFD600] text-[#222] border-none rounded px-4 py-2 font-semibold text-[15px] cursor-pointer"
              >
                + Add Row
              </button>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="bg-[#333] text-[#FFD600] border-none rounded px-4 py-2 font-semibold text-[15px] cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-[#FFD600] text-[#222] border-none rounded px-4 py-2 font-semibold text-[15px] cursor-pointer"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pie Chart Edit Modal */}
      {pieModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center">
          <div className="bg-[#181818] rounded-xl p-8 min-w-[500px] max-w-[90vw] shadow-2xl">
            <h2 className="text-[#FFD600] mb-4.5">
              Edit Smart Planning Activities
            </h2>
            <table className="w-full border-collapse mb-4.5">
              <thead>
                <tr className="text-[#FFD600] font-bold text-[15px]">
                  <th className="p-2 text-left">ACTIVITY</th>
                  <th className="p-2 text-left w-[120px]">
                    TIME IN A DAY (hrs)
                  </th>
                  <th className="p-2 text-left w-[100px]">COLOR</th>
                  <th className="w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {editPieRows.map((row, idx) => (
                  <tr key={idx}>
                    <td className="p-1.5">
                      <input
                        value={row.name}
                        onChange={(e) =>
                          handlePieRowChange(idx, "name", e.target.value)
                        }
                        className="w-full p-1.5 rounded border border-[#333] bg-[#222] text-white"
                        placeholder="Activity Name"
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        type="number"
                        min={1}
                        value={row.value}
                        onChange={(e) =>
                          handlePieRowChange(
                            idx,
                            "value",
                            Math.max(1, Number(e.target.value))
                          )
                        }
                        className="w-full p-1.5 rounded border border-[#333] bg-[#222] text-white"
                        placeholder="Hours"
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        type="color"
                        value={row.color}
                        onChange={(e) =>
                          handlePieRowChange(idx, "color", e.target.value)
                        }
                        className="w-9 h-9 border-none bg-transparent cursor-pointer"
                      />
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => handleDeletePieRow(idx)}
                        className="bg-transparent border-none text-[#FF4B4B] text-lg cursor-pointer flex items-center justify-center"
                        title="Delete Row"
                        disabled={editPieRows.length === 1}
                      >
                        <TrashIcon size={28} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-3 mb-4.5">
              <button
                onClick={handleAddPieRow}
                className="bg-[#FFD600] text-[#222] border-none rounded px-4 py-2 font-semibold text-[15px] cursor-pointer"
              >
                + Add Activity
              </button>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={closePieModal}
                className="bg-[#333] text-[#FFD600] border-none rounded px-4 py-2 font-semibold text-[15px] cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handlePieSave}
                className="bg-[#FFD600] text-[#222] border-none rounded px-4 py-2 font-semibold text-[15px] cursor-pointer"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficeNoticeBoard;
